import React, { useState, useEffect, useRef } from 'react';
import {
  Table,
  TableCell,
  TableRow,
  TableBody,
  TableHead,
  TableContainer,
  Button,
  IconButton,
  Typography,
  InputAdornment,
  TextField,
  Modal,
  Box
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import ArrowCircleRightIcon from '@mui/icons-material/ArrowCircleRight';
import ArrowCircleLeftIcon from '@mui/icons-material/ArrowCircleLeft';
import { useTheme as useAppTheme } from '../../../context/ThemeContext';
import { useApi } from 'context/ApiProvider';
import { useSelectedDesignStore } from 'context/SelectedDesignContext';
import { consumeDesignLoadPendingClose, ensureAtLeastOneDesign, fetchUserDesigns, getNewDesign, removeDesign } from 'services/designService';
import {
  isClearDatabaseSearch,
  VOICE_DATABASE_OPEN_EVENT,
  VOICE_DATABASE_QUERY_EVENT,
  VOICE_DESIGN_DELETED_EVENT,
  type VoiceDatabaseOpenDetail,
  type VoiceDatabaseQueryDetail,
  type VoiceDatabaseQueryResult,
} from 'Voice/voiceCommands';
import { SaveSettings, scheduleAutosave } from 'components/Viewer3d/SaveManager';
import { showAlertDialog } from 'components/Dialog/dialog';
import { PaginatedSearchRequestDto } from 'models/PaginatedSearchRequestDto';
import { useViewerStore } from 'context/ViewerContext';
import { useDesignStore } from 'context/DesignContext';
import { useAuth } from 'context/AuthContext';
import { LOCAL_STORAGE_KEYS } from 'components/Viewer3d/consts';
import { formatLocalDate } from 'Utils/StringUtils';
import { isDesktop } from 'Utils/MediaQueryUtils';
import { backendInsoleTypeMap, InsoleType } from 'models/Insole';
import { ApiDesignData } from 'models/ApiDesignData';
import { DesignCategory } from 'models/InsoleDesign';
import { ShoeIcon, SlipperIcon, HighHeelIcon } from 'components/Iconify/Iconify';
import { canUseDevelopmentUi } from 'auth/permissions';


interface DesignData {
  id: number;
  name: string;
  date: string;
  status: string;
  hasPressure: boolean;
  hasRondom: boolean;
  hasWBCT: boolean;
  printCount: number;
  category: DesignCategory;
}

interface DatabasePanelProps {
  isOpen: boolean;
  onCloseCallback: () => void;
}

const DatabasePanel: React.FC<DatabasePanelProps> = ({ isOpen, onCloseCallback }) => {
  const [animationClass, setAnimationClass] = useState('');
  const [selected, setSelected] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [designData, setDesignData] = useState<DesignData[]>([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagesToShow, setPagesToShow] = useState<(number | 'ellipsis')[]>([]);
  const [isActiveDesignRemoved, setActiveDesignRemoved] = React.useState(false);
  const [showFooterButtons, setShowFooterButtons] = React.useState(true);

  const prevPageNumber = useRef(pageNumber);
  const totalRecords = useRef(0);
  const initialLoad = useRef(false);
  const skipNextFetch = useRef(false);
  const totalPages = useRef<number>(1);
  const searchTermRef = useRef(searchTerm);
  const pageNumberRef = useRef(pageNumber);
  const lastFetchedPage = useRef(pageNumber);
  const fetchGen = useRef(0);
  const restoringLastDesign = useRef(true);
  const designDataRef = useRef<DesignData[]>([]);
  const populateRef = useRef<(customPage?: number, customSearch?: string) => Promise<DesignData[]>>(async () => []);
  const commitOpenRef = useRef<(id: number, rows?: DesignData[]) => Promise<void>>(async () => {});
  searchTermRef.current = searchTerm;
  pageNumberRef.current = pageNumber;
  designDataRef.current = designData;

  const { isDarkMode } = useAppTheme();
  const api = useApi();
  const designUpdated = useSelectedDesignStore((state) => state.designUpdated);
  const designLoaded = useDesignStore((state) => state.designLoaded);
  const pageSize = 10;
  const { user, isExhibition, updateExhibitionDesign } = useAuth();
  const canUseDevTools = canUseDevelopmentUi(user, isExhibition);


  const handleNextPage = () => {
    if (totalPages.current > 1 && pageNumber < totalPages.current) {
      setPageNumber((prev) => prev + 1);
    }
  };

  const handlePreviousPage = () => {
    if (pageNumber > 1) {
      setPageNumber((prev) => prev - 1);
    }
  };

  const handleSelectPage = (page: number) => {
    if (page === pageNumber) return;
    setPageNumber(page);
  };


  const onClose = async () => {
    if (isActiveDesignRemoved) {
      // if (useSelectedDesignStore.getState().designAdded == false) {
      await openDesign(null);
      // }
      setActiveDesignRemoved(false);
    }
    onCloseCallback();
  }

  const updatePageNumbers = () => {
    const tempPageNumbers: (number | 'ellipsis')[] = [];
    const total = totalPages.current || pageNumber;
    if (total <= 3) {
      for (let i = 1; i <= total; i++) tempPageNumbers.push(i);
    } else {
      if (pageNumber <= 2)
        tempPageNumbers.push(1, 2, 'ellipsis', total);
      else if (pageNumber < total - 1)
        tempPageNumbers.push(1, 'ellipsis', pageNumber, 'ellipsis', total);
      else if (pageNumber >= total - 1)
        tempPageNumbers.push(1, 'ellipsis', total - 1, total);
    }
    setPagesToShow(tempPageNumbers);
  }

  const populateDesignData = async (customPage?: number, customSearch?: string) => {
    if (!api) return [];
    const gen = ++fetchGen.current;
    setLoading(true);
    const targetPage = customPage || pageNumber;
    const term = customSearch !== undefined ? customSearch : searchTerm;
    const designRequestDto = new PaginatedSearchRequestDto({
      pageNumber: targetPage,
      pageSize,
      searchTerm: term,
    });
    const res = await fetchUserDesigns(api, designRequestDto);
    if (gen !== fetchGen.current) return [];
    if (!res) {
      setError('No response from server!');
      setDesignData([]);
      setLoading(false);
      setHasNextPage(false);
      return [];
    }
    if (!res.data || res.data.length < 1) {
      lastFetchedPage.current = targetPage;
      setError("No designs found for this page.");
      setDesignData([]);
      setLoading(false);
      setHasNextPage(false);
      return [];
    }
    totalPages.current = res.totalPages;
    totalRecords.current = res.totalRecords;
    lastFetchedPage.current = res.pageNumber || targetPage;
    if (res.pageNumber && res.pageNumber !== targetPage) {
      initialLoad.current = true;
      setPageNumber(res.pageNumber);
    }
    setHasNextPage(res.pageNumber < res.totalPages);

    const mappedData: DesignData[] = res.data.map((item: ApiDesignData) => ({
      id: item.designId,
      name: item.name,
      date: formatLocalDate(item.createdAt),
      status: (item.hasPressure ? 'Pressure' : item.hasRondom ? 'Rondom Pressure' : '')
        + ((item.hasPressure || item.hasRondom) && item.hasWBCT ? ' & ' : '')
        + (item.hasWBCT ? ' WBCT' : '')
        + (item.hasPressure || item.hasRondom || item.hasWBCT ? '' : 'Manual'),
      hasPressure: item.hasPressure,
      hasRondom: item.hasRondom,
      hasWBCT: item.hasWBCT,
      printCount: item.printCount,
      category: item.designCategory ?? DesignCategory.Insole,
    }));

    setDesignData(mappedData);
    designDataRef.current = mappedData;
    updatePageNumbers();
    setError(null);
    setLoading(false);
    return mappedData;
  }
  populateRef.current = populateDesignData;

  const updateHasPressure = (desings: DesignData[] | null = null, id: number | null = null) => {
    const temp = desings == null ? designData : desings;
    const thisId = id == null ? selected : id;
    const selectedData = temp.find(d => d.id === thisId);
    if (selectedData == undefined || selectedData == null) return;
    if (selectedData.hasPressure === true) {
      useSelectedDesignStore.setState({ hasPressure: true });
    }
    else {
      useSelectedDesignStore.setState({ hasPressure: false });
    }

    if (selectedData.hasRondom === true) {
      useSelectedDesignStore.setState({ hasRondomImage: true });
    }
    else {
      useSelectedDesignStore.setState({ hasRondomImage: false });
    }
  }

  const openDesign = async (designId: number | null = null) => {
    if (!api) return;

    if (designId !== null) {
      useSelectedDesignStore.getState().setDesignId(designId);
      setSelected(designId);
      return
    }

    try {
      let toLoadId = 0;
      if (designData.length > 0) {
        const lastOpened = localStorage.getItem(LOCAL_STORAGE_KEYS.LAST_OPENED_DESIGN);
        toLoadId = lastOpened ? JSON.parse(lastOpened).designId : designData[0].id;
      }
      if (user?.isAdmin === false) {
        useSelectedDesignStore.getState().setDesignId(toLoadId);
        setSelected(toLoadId);
      }
    } catch (error) {
      console.error('Failed to set the current design! Please try again later.', error);
    }
  };


  useEffect(() => {
    const restoreLastDesign = async () => {
      const stored = localStorage.getItem(LOCAL_STORAGE_KEYS.LAST_OPENED_DESIGN);
      if (!stored) {
        const designs = await populateDesignData();
        if (designs.length === 0) {
          bootstrapFirstDesign();
          return;
        }
        const latestId = designs[0].id;
        openDesign(latestId);
        updateHasPressure(designs, latestId);
        return;
      }

      const { designId, designPageNumber } = JSON.parse(stored);
      setPageNumber(designPageNumber);
      prevPageNumber.current = designPageNumber;
      const designs = await populateDesignData(designPageNumber);
      openDesign(designId);
      updateHasPressure(designs, designId);
    };

    void restoreLastDesign().finally(() => {
      restoringLastDesign.current = false;
    });
  }, []);

  useEffect(() => {
    if (restoringLastDesign.current) return;
    if (initialLoad.current) {
      initialLoad.current = false;
      return;
    }
    if (skipNextFetch.current) {
      skipNextFetch.current = false;
      return;
    }

    populateDesignData();
    prevPageNumber.current = pageNumber;
  }, [api, pageNumber]);

  useEffect(() => {
    const loaddesign = async () => {
      if (!designUpdated) return;
      useSelectedDesignStore.setState({ designUpdated: false });
    }
    loaddesign();
  }, [designUpdated])

  useEffect(() => {
    if (restoringLastDesign.current) return;
    if (skipNextFetch.current) {
      skipNextFetch.current = false;
      return;
    }
    populateDesignData();
  }, [searchTerm]);

  useEffect(() => {
    const onVoiceDeleted = (event: Event) => {
      const detail = (event as CustomEvent<{ id?: number; wasOpen?: boolean }>).detail;
      void populateDesignData();
      if (detail?.wasOpen) setActiveDesignRemoved(true);
    };
    const onVoiceOpen = (event: Event) => {
      const detail = (event as CustomEvent<VoiceDatabaseOpenDetail>).detail || {};
      void (async () => {
        const parsedId = detail.id !== undefined && Number.isFinite(Number(detail.id)) ? Number(detail.id) : undefined;
        const name = detail.name?.trim();
        let rows = designDataRef.current;
        let target = parsedId ? rows.find((row) => row.id === parsedId) : undefined;

        if (!target) {
          const term = parsedId !== undefined ? String(parsedId) : name || "";
          if (!term) {
            detail.resolve?.({ ok: false, error: "Provide a design id or name." });
            return;
          }
          if (term !== searchTermRef.current) skipNextFetch.current = true;
          if (pageNumberRef.current !== 1) initialLoad.current = true;
          setSearchTerm(parsedId !== undefined ? String(parsedId) : term);
          setPageNumber(1);
          rows = await populateRef.current(1, parsedId !== undefined ? String(parsedId) : term);
          if (parsedId !== undefined) {
            target = rows.find((row) => row.id === parsedId);
          } else {
            const compact = term.toLowerCase();
            const matches = rows.filter((row) => row.name.toLowerCase().includes(compact));
            if (matches.length > 1) {
              detail.resolve?.({
                ok: false,
                error: `Multiple designs match "${term}": ${matches.map((row) => `${row.name} (id ${row.id})`).join("; ")}. Call loadDesign with id.`,
              });
              return;
            }
            target = matches[0];
          }
        }

        if (!target) {
          detail.resolve?.({
            ok: false,
            error: parsedId !== undefined ? `No design with id ${parsedId}.` : `No design named "${name}".`,
          });
          return;
        }

        await commitOpenRef.current(target.id, rows.includes(target) ? rows : [...rows, target]);
        detail.resolve?.({ ok: true, id: target.id, name: target.name });
      })();
    };
    const onVoiceQuery = (event: Event) => {
      const detail = (event as CustomEvent<VoiceDatabaseQueryDetail>).detail || {};
      void (async () => {
        let nextSearch = searchTermRef.current;
        if (detail.search !== undefined && detail.search !== null) {
          nextSearch = isClearDatabaseSearch(String(detail.search)) ? "" : String(detail.search).trim();
        }
        let nextPage = pageNumberRef.current;
        if (detail.page === "next") {
          nextPage = Math.min(pageNumberRef.current + 1, Math.max(1, totalPages.current || 1));
        } else if (detail.page === "prev") nextPage = Math.max(1, pageNumberRef.current - 1);
        else if (typeof detail.page === "number") nextPage = Math.max(1, detail.page);
        else if (detail.search !== undefined) nextPage = 1;

        if (nextSearch !== searchTermRef.current) skipNextFetch.current = true;
        if (nextPage !== pageNumberRef.current) initialLoad.current = true;
        setSearchTerm(nextSearch);
        setPageNumber(nextPage);
        const rows = await populateRef.current(nextPage, nextSearch);
        const result: VoiceDatabaseQueryResult = {
          search: nextSearch,
          page: lastFetchedPage.current || nextPage,
          totalPages: totalPages.current || 1,
          totalRecords: totalRecords.current,
          designs: rows.map((row) => ({ id: row.id, name: row.name })),
          error: rows.length ? undefined : "empty",
        };
        detail.resolve?.(result);
      })();
    };
    document.addEventListener(VOICE_DESIGN_DELETED_EVENT, onVoiceDeleted);
    document.addEventListener(VOICE_DATABASE_QUERY_EVENT, onVoiceQuery);
    document.addEventListener(VOICE_DATABASE_OPEN_EVENT, onVoiceOpen);
    return () => {
      document.removeEventListener(VOICE_DESIGN_DELETED_EVENT, onVoiceDeleted);
      document.removeEventListener(VOICE_DATABASE_QUERY_EVENT, onVoiceQuery);
      document.removeEventListener(VOICE_DATABASE_OPEN_EVENT, onVoiceOpen);
    };
  }, []);

  useEffect(() => {
    if (isOpen) {
      setAnimationClass('database-panel-visible');
      populateDesignData(pageNumber);
      setShowFooterButtons(isDesktop());
    } else {
      setAnimationClass('database-panel-hidden');
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !designLoaded) return;
    if (consumeDesignLoadPendingClose()) onCloseCallback();
  }, [designLoaded, isOpen]);

  const handleRowClick = (id: number) => {
    setSelected(id);
  };

  const commitOpenDesign = (id: number, rows?: DesignData[]) => {
    const list = rows ?? designDataRef.current;
    setSelected(id);
    localStorage.setItem(
      LOCAL_STORAGE_KEYS.LAST_OPENED_DESIGN,
      JSON.stringify({ designId: id, designPageNumber: pageNumberRef.current })
    );
    return scheduleAutosave('Current design', {
      now: true
    } as Partial<SaveSettings>).then(() => {
      useSelectedDesignStore.getState().setDesignId(id);
      updateHasPressure(list, id);
      onClose();
    });
  };
  commitOpenRef.current = commitOpenDesign;

  if (!isOpen && animationClass === 'database-panel-hidden') {
    return null;
  }

  const handleOpen = () => {
    scheduleAutosave('Current design', {
      now: true
    } as Partial<SaveSettings>).then(() => {
      useSelectedDesignStore.getState().setDesignId(selected === null ? -1 : selected);
      updateHasPressure();
      let storageData = { designId: selected === null ? -1 : selected, designPageNumber: pageNumber };
      localStorage.setItem(LOCAL_STORAGE_KEYS.LAST_OPENED_DESIGN, JSON.stringify(storageData));
      onClose();
    });
  }

  const applyOpenedDesign = (dId: number) => {
    if (dId < 1) return;
    setSelected(dId);
    useSelectedDesignStore.setState({ designId: dId });
    useSelectedDesignStore.setState({ designUpdated: true });
    useViewerStore.setState({ newDesign: true });
    useSelectedDesignStore.setState({ hasPressure: false });
    useSelectedDesignStore.setState({ hasRondomImage: false });
    let storageData = { designId: dId, designPageNumber: pageNumber };
    localStorage.setItem(LOCAL_STORAGE_KEYS.LAST_OPENED_DESIGN, JSON.stringify(storageData));
    if (isExhibition) updateExhibitionDesign(dId);
  };

  const bootstrapFirstDesign = async () => {
    setPageNumber(1);
    localStorage.removeItem(LOCAL_STORAGE_KEYS.LAST_OPENED_DESIGN);
    try {
      const templateName = backendInsoleTypeMap[InsoleType.Standard];
      const dId = await ensureAtLeastOneDesign(api, templateName);
      applyOpenedDesign(dId);
      populateDesignData();
      onCloseCallback();
    } catch (error) {
      console.error("Error Opening new template design", error);
    }
  };

  const handleNewDesign = () => {
    if (designData.length === 0) bootstrapFirstDesign();
    else {
      scheduleAutosave('Current design', {
        now: true
      } as Partial<SaveSettings>).then(() => {
        newDesignClicked();
      });
    }
  }

  const newDesignClicked = async () => {
    setPageNumber(1);
    localStorage.removeItem(LOCAL_STORAGE_KEYS.LAST_OPENED_DESIGN);

    try {
      const templateName = backendInsoleTypeMap[InsoleType.Standard];
      await getNewDesign(api, templateName).then((dId: number) => {
        applyOpenedDesign(dId);
        populateDesignData();
        onCloseCallback();
      });
    } catch (error) {
      console.error("Error Opening new template design", error);
    }
  }

  const handleDoubleClick = (id: number) => {
    void commitOpenDesign(id);
  }

  const onDeleteClickHandler = async (id: number) => {
    setSelected(id);

    const result = await showAlertDialog({
      title: "Removing a Design",
      description: "Are you sure to remove this design?",
      agreeText: "Yes",
      disagreeText: "No",
    });

    if (!result) return;

    try {
      const res = await removeDesign(api, id);
      if (!res) return;

      await populateDesignData();

      if (useSelectedDesignStore.getState().designId === id) {
        localStorage.removeItem(LOCAL_STORAGE_KEYS.LAST_OPENED_DESIGN);
        setActiveDesignRemoved(true);
      }

    } catch (err) {
      console.error("Error removing design:", err);
    }
  };

  return (
    <Modal open={isOpen} onClose={onClose} closeAfterTransition sx={{ display: "flex", alignItems: "center", justifyContent: "center", }}>
      <Box
        sx={{
          bgcolor: isDarkMode ? '#333' : '#fff',
          color: isDarkMode ? '#fff' : '#000',
          borderRadius: 2,
          width: { xs: '95vw', md: '95vw', lg: '50vw' },
          maxHeight: { xs: '95vh', md: '95vh', lg: '80vh' },
          mx: 'auto',
          boxShadow: 24,
          outline: 'none',
          display: 'flex',
          flexDirection: 'column',
          p: 0,
          position: 'relative',
        }}
      >

        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, borderBottom: '1px solid #ccc' }}>
          <Typography variant="h6" sx={{ color: isDarkMode ? '#fff' : '#076596' }}>
            Designs
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon sx={{ color: isDarkMode ? '#fff' : '#000' }} />
          </IconButton>
        </Box>

        {/* Content */}
        <Box
          sx={{
            flexGrow: 1,
            overflowY: 'auto',
            p: 3,
          }}
        >
          <Box
            className="database-panel-search-bar"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 1,
            }}
          >
            <TextField
              fullWidth
              size="small"
              placeholder="Search designs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{
                color: 'var(--text-color)',
                '& .MuiInputBase-input': {
                  color: 'var(--text-color)',
                },
                '& .MuiInputBase-input::placeholder': {
                  color: 'var(--text-color-secondary)',
                  opacity: 1,
                },
                'fieldset': {
                  border: '1px solid var(--toolbar-border)'
                },
              }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: 'var(--text-color)' }} />
                    </InputAdornment>
                  ),
                  enterKeyHint: 'send',
                }
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  (document.activeElement as HTMLElement)?.blur();
                }
              }}
            />
          </Box>

          <Box
            sx={{
              border: '1px solid #ccc',
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            <TableContainer>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow
                    sx={{
                      '& th': {
                        bgcolor: '#f0f0f0',
                        fontWeight: 700,
                        borderBottom: '1px solid #ccc',
                      },
                    }}
                  >
                    <TableCell width="15%">ID</TableCell>
                    {canUseDevTools && <TableCell width="10%">Type</TableCell>}
                    <TableCell width={canUseDevTools ? "25%" : "30%"}>Name</TableCell>
                    <TableCell width="25%">Date</TableCell>
                    <TableCell width={canUseDevTools ? "20%" : "25%"}>Data</TableCell>
                    <TableCell width="5%" align="center">
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography
                          sx={{
                            color: isDarkMode ? '#fff' : '#000',
                            fontSize: '1rem',
                            fontWeight: 500,
                            padding: '16px',
                          }}
                        >
                          Loading...
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : error || designData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography
                          sx={{
                            color: isDarkMode ? '#ff6b6b' : '#e74c3c',
                            fontSize: '1rem',
                            fontWeight: 500,
                            padding: '16px',
                          }}
                        >
                          {error || 'No matching designs found'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    designData.map((row) => {
                      // const isPressure = ['pressure', 'rondom pressure'].includes(row.status.toLowerCase());
                      const isItemSelected = selected === row.id;
                      const isPressure = row.hasPressure || row.hasRondom;
                      const isWBCT = row.hasWBCT;
                      const isBoth = isWBCT && isPressure;

                      return (
                        <TableRow
                          key={row.id}
                          hover
                          selected={isItemSelected}
                          onClick={() => handleRowClick(row.id)}
                          onDoubleClick={() => handleDoubleClick(row.id)}
                          sx={{
                            cursor: 'pointer', '& .MuiTableCell-root': {
                              color: isDarkMode ? '#fff' : '#000'
                            }
                          }}
                        >
                          <TableCell>{row.id}</TableCell>
                          {canUseDevTools && <TableCell>
                            <IconButton sx={{ color: 'var(--text-color)' }}>
                              {row.category == DesignCategory.Insole ? <ShoeIcon /> :
                                row.category == DesignCategory.HighHeel ? <HighHeelIcon /> :
                                  <SlipperIcon />}
                            </IconButton>

                          </TableCell>}
                          <TableCell>{row.name}</TableCell>
                          <TableCell>{row.date}</TableCell>

                          <TableCell>
                            <Box
                              sx={{
                                display: 'inline-block',
                                px: 1.5,
                                py: 0.5,
                                borderRadius: 1,
                                fontWeight: 600,
                                fontSize: '0.75rem',
                                bgcolor: isBoth ? 'rgba(230, 126, 34, 0.25)'
                                  : isPressure ? 'rgba(46, 204, 113, 0.25)'
                                    : isWBCT ? 'rgba(231, 76, 60, 0.5)'
                                      : 'rgba(52, 152, 219, 0.25)',
                                color: (isBoth && !isDarkMode) ? '#2c3e50' : (isBoth && isDarkMode) ? '#ffffff' : isPressure ? '#2ecc71' : isWBCT ? '#ffffff' : '#3498db',
                              }}
                            >
                              {row.status}
                            </Box>
                          </TableCell>

                          <TableCell align="center">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                onDeleteClickHandler(row.id);
                              }}
                              sx={{
                                color: '#e53935',
                                '&:hover': { color: '#b71c1c' },
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )
                  }
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>

        {/* Footer */}
        <Box
          sx={{
            p: 2,
            borderTop: '1px solid #ccc',
            display: 'flex',
            justifyContent: showFooterButtons ? 'space-between' : 'center',
            alignItems: 'center',
          }}
        >
          {/* Left side - Pagination */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="text"
              disabled={!!error || pageNumber <= 1}
              onClick={handlePreviousPage}
              sx={{
                minWidth: 36,
                color: 'var(--accent-color)',
                '&.Mui-disabled': {
                  color: 'var(--text-color-disabled)',
                },
              }}
            >
              <ArrowCircleLeftIcon />
            </Button>

            {/* Page Numbers */}
            {
              !error && pagesToShow.map((p, idx) =>
                p === 'ellipsis' ? (
                  <Box key={`ellipsis-${idx}`} sx={{ px: 1 }}>...</Box>
                ) : (
                  <Button
                    key={`page-${idx}`}
                    variant="text"
                    disabled={p === pageNumber}
                    onClick={() => handleSelectPage(p)}
                    sx={{
                      minWidth: 36,
                      fontWeight: p === pageNumber ? 700 : 500,
                      color: 'var(--text-color)',
                      '&.Mui-disabled': {
                        color: 'var(--text-color-disabled)',
                      },
                    }}
                  >
                    {p}
                  </Button>
                )
              )
            }
            <Button
              variant="text"
              disabled={!!error || !hasNextPage}
              onClick={handleNextPage}
              sx={{
                minWidth: 36,
                color: 'var(--accent-color)',
                '&.Mui-disabled': {
                  color: 'var(--text-color-disabled)',
                },
              }}
            >
              <ArrowCircleRightIcon />
            </Button>
          </Box>

          {/* Right side - Actions */}
          {
            showFooterButtons && <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                onClick={handleNewDesign}
                sx={{
                  color: isDarkMode ? '#fff' : '#000',
                  borderColor: '#aaa',
                  fontWeight: 500,
                }}
              >
                + New
              </Button>

              <Button
                variant="contained"
                sx={{
                  color: '#fff',
                  backgroundColor: '#076596',
                  width: '80px',
                  fontWeight: 500,
                  px: 4,
                  '&:hover': { backgroundColor: '#0a6fb4' },
                }}
                disabled={selected === null}
                onClick={handleOpen}
              >
                Open
              </Button>
            </Box>
          }
        </Box>
      </Box>
    </Modal>
  );

};

export default DatabasePanel;
